import { getStageFromHostname } from "config/stageConfig";

// biome-ignore lint/complexity/noStaticOnlyClass: existing pattern
export class GaiaConstants {
  static readonly SELECTED_TEAM_NAME = "Team";
  static readonly GUEST_TEAM = "guest";
  static readonly SELECTED_TEAM_DESCRIPTION = "TeamDescription";
  static readonly SELECTED_SUB_TEAM_NAME = "SubTeam";
  // Data related pages/components
  static readonly CUSTOMER_INFORMATION_LOOKUP = "CustomerInformationLookup";
  static readonly CUSTOMER_INFORMATION_LOOKUP_BY_BRAAVOS =
    "CustomerInformationLookupByBraavos";
  static readonly SELLER_INFORMATION_LOOKUP = "SellerInformationLookup";
  static readonly BRAZIL_MARKET_PLACE_SELLER_LOOKUP =
    "BrazilMarketPlaceSellerLookup";
  static readonly BULK_UPLOAD_WORKFLOW = "BulkUploadWorkflow";
  static readonly ATLAS_CHATBOT_CONVERSATIONS_LIMIT = 10;
  static readonly ATLAS_CHATBOT_MESSAGES_LIMIT = 10;
  static readonly NOTIFICATION_TIMEOUT = {
    SHORT: 5000,
    LONG: 10000,
  };
  static readonly OPENSEARCH_SYNC_DELAY = 2500; // for DDB->OpenSearch sync
  // Budget for an index-backed read to return a row that was just written.
  static readonly NEW_ROW_INDEX_WAIT = {
    POLL_MS: 1000,
    GIVE_UP_MS: 5000,
  };
  static readonly PLACE_HOLDER = "placeholder";
  static readonly INDIA_DIRECT_TEAM = "india_direct";
  static readonly US_SALT_DIRECT_TEAM = "us_salt_direct";
  static readonly US_PROPERTY_TAX_TEAM = "us_property_tax";
  static readonly EMEA_STORES_TEAM = "emea_stores";
  static readonly AUDIT_FILES_PREFIX = "audits";
  static readonly TEMPLATE_TEAMS = ["us_salt_direct", "us_salt_indirect"];
  static readonly TABLE_FILTER_OPERATORS = ["=", "!=", ":", "!:", "^", "!^"];
  static readonly DOCUMENT_API_BATCH_SIZE = 10;
  static readonly DOCUMENT_STORE_DISCLAIMER = "Gaia keeps files for 10 years.";
  static readonly DEFAULT_STATUS = "Not started";
  static readonly FOLDER_EXTENSION_TYPE = "Folder";
  static readonly LINK_EXTENSION_TYPE = "Link";
  static readonly DEFAULT_UNIQUE_VALUES_LIMIT = 150;
  // Draft provenance, matching the service's ScanJobSource values.
  static readonly NOVA_SOURCE = "Nova";
  static readonly GAIA_SOURCE = "Gaia";
  static readonly EMAIL_SOURCE = "Email";
}

export const TAB_ID_QUERY_PARAM = "tabId";
export const SUB_TAB_ID_QUERY_PARAM = "subTabId";

// Keep TEAM_LDAP_STEM_MAP below in sync; a covering test enforces it.
export const TEAM_NAME_MAP: {
  [key: string]: string;
} = {
  accounting: "Accounting",
  advertising: "Advertising",
  apac_accounting_compliance: "APAC Accounting Compliance",
  tax_controversy: "Tax Controversy",
  aws_emea_tax_planning: "AWS EMEA Tax Planning",
  aws_apac_tax_planning: "AWS APAC Tax Planning",
  aws_latam_tax_planning: "AWS LATAM Tax Planning",
  brazil_indirect: "Brazil Indirect",
  canada: "Canada",
  digital_devices: "Digital & Devices",
  gaia_dev: "Gaia Dev",
  germany_rc: "Germany R&C",
  emea_accounting_compliance: "EMEA Accounting Compliance",
  emea_stores: "EMEA Stores",
  gtd_audit: "GTD Audit",
  india_direct: "India Direct",
  india_wht: "India WHT",
  international_tax_planning_mexico: "International Tax Planning Mexico",
  ireland_ccf: "Ireland - Tax Authority Management (CCF)",
  itx_compliance: "ITX Compliance",
  us_property_tax: "US Property Tax",
  us_state_rc: "US State R&C",
  latam_accounting_compliance: "LATAM Accounting Compliance",
  tax_engine: "Tax Engine",
  us_fed_rc: "US Fed R&C",
  us_salt_direct: "US SaLT Direct",
  us_salt_indirect: "US SaLT Indirect",
  us_salt_itc: "US SaLT ITC",
};

// Full Gaia team list for the cross-team Assignee team dropdown; ListTeams is membership-scoped so we surface every team here.
export const GAIA_TEAM_OPTIONS: Array<{ label: string; value: string }> = [
  { label: "\u00A0", value: "\u00A0" },
  ...Object.entries(TEAM_NAME_MAP)
    .map(([value, label]) => ({ label, value }))
    .sort((a, b) => a.label.localeCompare(b.label)),
];

// Team id -> LDAP group stem. The full group id is `fintech-gaia-<stem>` in
// prod and `test-fintech-gaia-<stem>` in non-prod stages.
export const TEAM_LDAP_STEM_MAP: { [teamId: string]: string } = {
  accounting: "accounting",
  advertising: "advertising",
  apac_accounting_compliance: "apacaccountingcompliance",
  aws_apac_tax_planning: "awsapactaxplanning",
  aws_emea_tax_planning: "awsemeataxplanning",
  aws_latam_tax_planning: "awslatamtaxplanning",
  brazil_indirect: "brazilindirect",
  canada: "canada",
  digital_devices: "digitaldevices",
  emea_accounting_compliance: "emeaaccountingcompliance",
  emea_stores: "emeastores",
  gaia_dev: "dev",
  germany_rc: "emearc",
  gtd_audit: "gtdaudit",
  india_direct: "indiadirect",
  india_wht: "indiawht",
  international_tax_planning_mexico: "intltaxplanningmexico",
  ireland_ccf: "irelandccf",
  itx_compliance: "itxcompliance",
  latam_accounting_compliance: "latamaccountingcompliance",
  tax_controversy: "taxcontroversy",
  tax_engine: "taxengine",
  us_fed_rc: "usfedrc",
  us_property_tax: "propertytax",
  us_salt_direct: "ussaltdirect",
  us_salt_indirect: "ussaltindirect",
  us_salt_itc: "ussaltitc",
  us_state_rc: "usdirectrc",
};

// Builds a permissions.amazon.com link from a team id via TEAM_LDAP_STEM_MAP.
// Returns "" for unknown or missing team ids. Prefer this when the caller
// has a team id but no ldapGroupId (e.g. cross-team assignee dropdown).
export const buildTeamPermissionsLink = (
  teamId: string | null | undefined,
  suffix: string = "_Team",
): string => {
  const stem = teamId ? TEAM_LDAP_STEM_MAP[teamId] : undefined;
  if (!stem) return "";
  const prefix = getStageFromHostname() === "prod" ? "Gaia_" : "GaiaTest_";
  return `https://permissions.amazon.com/a/team/${prefix}${stem}${suffix}`;
};

// Variant of buildTeamPermissionsLink for callers that already have the full
// ldapGroupId (e.g. a GetTeam response). Works for sub-teams too.
export const buildTeamPermissionsLinkFromLdapGroupId = (
  ldapGroupId: string | null | undefined,
  suffix: string = "_Team",
): string => {
  if (!ldapGroupId) return "";
  const permissionsTeamName = ldapGroupId
    .replace("test-fintech-gaia-", "GaiaTest_")
    .replace("fintech-gaia-", "Gaia_");
  return `https://permissions.amazon.com/a/team/${permissionsTeamName}${suffix}`;
};

// Builds a PhoneTool link for an employee alias. Returns "" for a missing
// alias so callers can fall back to plain text.
export const buildPhoneToolLink = (alias: string | null | undefined): string =>
  alias
    ? `https://atoz.amazon.work/phonetool/users/${encodeURIComponent(alias)}`
    : "";

export const GAIAOBJECT_CATEGORIES = ["audit", "notice", "litigation"] as const;

export type GAIAOBJECT_CATEGORY = (typeof GAIAOBJECT_CATEGORIES)[number];

// A resourceId for an audit, notice or litigation gaia object (not a task/doc).
export const isGaiaObjectResource = (resourceId: string): boolean =>
  GAIAOBJECT_CATEGORIES.some((prefix) => resourceId.startsWith(prefix));

// Prefix for client-minted OutcomeBreakdown IDs. Client-generated UUIDs are
// prefixed with this string so the server can distinguish caller-provided IDs
// from ones it needs to mint. Matches the backend `outcomebreakdown` prefix.
export const OUTCOME_BREAKDOWN_PREFIX = "outcomebreakdown";
